import React, { useState, useContext, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "react-bootstrap";
import { FaRegBell } from "react-icons/fa";
import { AuthContext } from "../Context/AuthContext";
import useTaskNotificationCount from "./useTaskNotificationCount";
import { format, formatDistanceToNow } from "date-fns";
import { db } from "../firebase.config.js";
import { collection, query, where, onSnapshot, orderBy, limit, writeBatch, doc } from "firebase/firestore";
import { formatNotificationUpdatedAt, normalizeUpdatedAtToDate } from "../utils/dateFormat";

// 📋 SHARED QUERY CONFIGURATION
const NOTIFICATION_COLLECTION = "document_tasks";

/**
 * Builds a query for unread completed/failed notifications (for dropdown listing)
 */
const buildNotificationQuery = (orgId, forDropdown = false) => {
   const baseQuery = query(
      collection(db, NOTIFICATION_COLLECTION),
      where("bucket", "==", orgId),
      where("status", "in", ["completed", "failed"]),  // Completed or failed documents
      where("readAt", "==", false)                      // That are unread
   );

   if (forDropdown) {
      return query(baseQuery, orderBy("doc_upload_date", "desc"), limit(30));
   }

   return baseQuery;
};

/**
 * Builds a query for processing documents
 * Matches documents where status is NOT "completed" or "failed"
 */
const buildProcessingQuery = (orgId) => {
   return query(
      collection(db, NOTIFICATION_COLLECTION),
      where("bucket", "==", orgId),
      where("status", "==", "processing")
   );
};

/**
 * Builds a query for dropdown list: all documents for org.
 * No orderBy so docs without updatedAt are included; sort client-side.
 */
const buildDropdownQuery = (orgId) => {
   return query(
      collection(db, NOTIFICATION_COLLECTION),
      where("bucket", "==", orgId),
      limit(30)
   );
};

/**
 * NotificationBell Component
 *
 * Displays ONE of three states (mutually exclusive):
 * 1. Spinner - when documents are processing (status == "processing")
 * 2. Bell + Count Badge - when documents are unread (readAt == false) and nothing processing
 * 3. Plain Bell - when all documents are read or none exist
 *
 * Auth-safe: Only renders and queries Firestore when authReady === true
 */
export default function NotificationBell() {
   const { authReady, firebaseUser, orgInfo } = useContext(AuthContext);
   const canListen = authReady && !!firebaseUser;
   const orgBucketId = canListen ? (orgInfo?.orgBucketId || null) : null;

   // State for unread counts (from hook) - separate counts for completed and failed
   const { failedCount, loading, error } = useTaskNotificationCount(orgBucketId, NOTIFICATION_COLLECTION);
   

   // State for processing count (real-time listener)
   const [processingCount, setProcessingCount] = useState(0);
   const [processingLoading, setProcessingLoading] = useState(true);
   const processingUnsubscribeRef = useRef(null);

   const [dropdownOpen, setDropdownOpen] = useState(false);
   const [dropdownDocuments, setDropdownDocuments] = useState([]);
   const dropdownUnsubscribeRef = useRef(null);
   const dropdownMenuRef = useRef(null);

   // Real-time listener for processing documents
   useEffect(() => {

   if (!canListen) {
         setProcessingCount(0);
         setProcessingLoading(false);
         return;
      }

      const q = buildProcessingQuery(orgBucketId);

      processingUnsubscribeRef.current = onSnapshot(
         q,
         (snapshot) => {
            setProcessingCount(snapshot.size);
            setProcessingLoading(false);
         },
         (err) => {
            console.error("Processing query error:", err);
            setProcessingCount(0);
            setProcessingLoading(false);
         }
      );

      return () => {
         if (processingUnsubscribeRef.current) {
            processingUnsubscribeRef.current();
         }
      };

   }, [canListen]);

   useEffect(() => {
   if (!authReady || !firebaseUser) {
      if (processingUnsubscribeRef.current) {
         processingUnsubscribeRef.current();
         processingUnsubscribeRef.current = null;
      }

      if (dropdownUnsubscribeRef.current) {
         dropdownUnsubscribeRef.current();
         dropdownUnsubscribeRef.current = null;
      }

      setProcessingCount(0);
      setProcessingLoading(false);
      setDropdownDocuments([]);
      setDropdownOpen(false);
   }
   }, [authReady, firebaseUser]);

   // Log errors silently
   useEffect(() => {
      if (error) {
         console.error("Notification count error:", error);
      }
   }, [error]);

   // 🔒 AUTH-SAFE: Handle auth state changes (logout, etc.)
   useEffect(() => {
      if (!authReady) {
         if (dropdownUnsubscribeRef.current) {
            dropdownUnsubscribeRef.current();
            dropdownUnsubscribeRef.current = null;
         }
         setDropdownOpen(false);
         setDropdownDocuments([]);
      }
   }, [authReady]);

   // Don't render if auth not ready
   if (!canListen) {
      return null;
   }

   console.log("🔔 NotificationBell auth state", {
  authReady,
  firebaseUser: !!firebaseUser
});

   // Mark all unread documents as read
   const markAllAsRead = async (documents) => {
      if (!documents || documents.length === 0) return;

      try {
         const batch = writeBatch(db);
         documents.forEach((docData) => {
            if (docData.readAt === false) {
               const docRef = doc(db, NOTIFICATION_COLLECTION, docData.id);
               batch.update(docRef, { readAt: true });
            }
         });
         await batch.commit();
         console.debug("🔔 Marked all notifications as read");
      } catch (err) {
         console.error("Error marking notifications as read:", err);
      }
   };

   // Handle dropdown toggle - with auth safety checks
   const handleDropdownToggle = (nextShow, event, metadata) => {
      // 🚪 Clicked outside → CLOSE and stop
      if (metadata?.source === "rootClose") {
        setDropdownOpen(false);
    
        if (dropdownUnsubscribeRef.current) {
          dropdownUnsubscribeRef.current();
          dropdownUnsubscribeRef.current = null;
        }
    
        return;
      }
    
      setDropdownOpen(nextShow);
    
      if (nextShow) {
        if (!canListen) return;
    
        const q = buildDropdownQuery(orgBucketId);
    
        if (dropdownUnsubscribeRef.current) {
          dropdownUnsubscribeRef.current();
        }
    
        dropdownUnsubscribeRef.current = onSnapshot(
          q,
          (snapshot) => {
            const raw = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
    
            const withDate = raw.map((item) => ({
              ...item,
              _updatedAtDate: normalizeUpdatedAtToDate(item.updatedAt),
            }));
    
            withDate.sort((a, b) => {
              const aVal = a._updatedAtDate?.getTime() ?? -Infinity;
              const bVal = b._updatedAtDate?.getTime() ?? -Infinity;
              if (aVal === -Infinity && bVal === -Infinity) return 0;
              if (aVal === -Infinity) return 1;
              if (bVal === -Infinity) return -1;
              return bVal - aVal;
            });
    
            setDropdownDocuments(withDate);
            markAllAsRead(withDate);
          },
          (err) => {
            console.error("Error fetching dropdown documents:", err);
            setDropdownDocuments([]);
          }
        );
      } else {
        if (dropdownUnsubscribeRef.current) {
          dropdownUnsubscribeRef.current();
          dropdownUnsubscribeRef.current = null;
        }
      }
    };
    



   // Cleanup on unmount
   useEffect(() => {
      return () => {
         if (dropdownUnsubscribeRef.current) {
            dropdownUnsubscribeRef.current();
         }
         if (processingUnsubscribeRef.current) {
            processingUnsubscribeRef.current();
         }
      };
   }, []);

   const formatNotificationTime = (date) => {
      if (!date) return "Unknown";
      
      try {
         const dateObj = date?.toDate?.() || new Date(date);
         if (isNaN(dateObj.getTime())) return "Invalid date";
         
         const now = new Date();
         const diffMs = now - dateObj;
         const diffMins = Math.floor(diffMs / 60000);
         
         if (diffMins < 1) return "Just now";
         if (diffMins < 60) return `${diffMins}m ago`;
         if (diffMins < 1440) return formatDistanceToNow(dateObj, { addSuffix: true });
         
         return format(dateObj, "MMM dd, yyyy HH:mm");
      } catch (error) {
         return "Invalid date";
      }
   };

   // CustomToggle component matching the existing notification bell
   const CustomToggle = React.forwardRef(({ children, onClick }, ref) => (
      <Link
         to=""
         ref={ref}
         onClick={(e) => {
            e.preventDefault();
            onClick(e);
         }}
         className="dropdown-link">
         {children}
      </Link>
   ));

   // Determine which state to show
   const isProcessing = processingCount > 0;

   // Render the appropriate icon based on state
   // All states occupy the SAME position (mutually exclusive)
   const renderNotificationIcon = () => {
      // Inline styles for container
      const containerStyle = {
         position: "relative",
         display: "inline-flex",
         alignItems: "center",
         justifyContent: "center",
         width: "39px",
         height: "39px",
      };

      // Inline styles for badge (centered over spinner)
      // Matches original: white background with colored border
      const badgeColor = failedCount > 0 ? "red" : "green";
      const badgeStyle = {
         position: "absolute",
         top: "50%",
         left: "50%",
         transform: "translate(-50%, -50%)",
         zIndex: 10,
         width: "32px",
         height: "32px",
         borderRadius: "50%",
         display: "flex",
         alignItems: "center",
         justifyContent: "center",
         fontSize: "0.875rem",
         fontWeight: 600,
         backgroundColor: "white",
         border: `2px solid ${badgeColor}`,
         color: badgeColor,
      };

      // Spinner style - needs position for z-index to work
      const spinnerStyle = {
         width: "39px",
         height: "39px",
         position: "absolute",
         top: 0,
         left: 0,
         zIndex: 1,
      };

      // State 1: Processing→ spinner with processing count badge inside
      if (isProcessing) {
         return (
            <div style={containerStyle}>
               <div
                  className="spinner-border spinner-color"
                  role="status"
                  style={spinnerStyle}
               />
               <div style={badgeStyle}>
                  <span>{processingCount > 20 ? "20+" : processingCount}</span>
               </div>
            </div>
         );
      }


      // State 4: No processing + no unread → plain bell
      return <FaRegBell size={24} />;
   };

   console.log("NotificationBell listener state", {
      authReady,
      firebaseUser: !!firebaseUser,
      canListen,
   });

   return (
      <Dropdown
   show={dropdownOpen}
   onToggle={handleDropdownToggle}
   autoClose="outside"
   align="end"
   className="dropdown-notification ms-3 ms-xl-4 d-flex align-items-center">
         <Dropdown.Toggle as={CustomToggle}>
            {renderNotificationIcon()}
         </Dropdown.Toggle>
         <Dropdown.Menu
            ref={dropdownMenuRef}
            className="mt-10-f me--10-f"
            style={{
               width: "380px",
               maxHeight: "min(90vh, calc(100vh - 2rem))",
               overflow: "hidden",
            }}>
            <div className="dropdown-menu-header">
               <h6 className="dropdown-menu-title">Notifications</h6>
            </div>
            
            {dropdownDocuments.length === 0 ? (
               <div className="list-group">
                  <div className="list-group-item text-center py-4 text-muted">
                     No documents
                  </div>
               </div>
            ) : (
               <div
                  className="dropdown-list-scroll"
                  style={{ maxHeight: "min(70vh, 480px)", overflowY: "auto" }}>
                  {dropdownDocuments.map((item) => {
                     const docName = item.metadata?.doc_orig_name ?? "";
                     const ocrType = item.metadata?.ocr_type ?? "";
                     const status = item.status ?? "";
                     const updatedAtStr = formatNotificationUpdatedAt(item._updatedAtDate);
                     return (
                        <div
                           key={item.id}
                           className="dropdown-list-item py-2 px-3 border-bottom border-light"
                           style={{ minHeight: "44px" }}>
                           <div className="d-flex justify-content-between align-items-center gap-2 small">
                              <div
                                 className="text-truncate fw-semibold flex-grow-1"
                                 style={{
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    minWidth: 0,
                                 }}
                                 title={docName}>
                                 {docName}
                              </div>
                              <div className="text-muted text-end flex-shrink-0">{ocrType}</div>
                           </div>
                           <div className="d-flex justify-content-between align-items-center gap-2 small text-muted mt-0">
                              <div className="text-truncate" style={{ minWidth: 0 }}>{status}</div>
                              <div className="text-end flex-shrink-0">{updatedAtStr}</div>
                           </div>
                        </div>
                     );
                  })}
               </div>
            )}
            
            <div className="dropdown-menu-footer">
               <Link to="#"></Link>
            </div>
         </Dropdown.Menu>
      </Dropdown>
   );
}
